import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
// useMutation removed - using Convex
import { useAction, useQuery as useConvexQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import clsx from 'clsx';

export const Route = createFileRoute('/recipes/$recipeId/')({
  component: RecipeDetailPage,
});

// Timer modal component
function TimerModal({ 
  duration, 
  label,
  onClose 
}: { 
  duration: number; // in seconds
  label: string;
  onClose: () => void;
}) {
  const [remaining, setRemaining] = useState(duration);
  const [isPaused, setIsPaused] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    if (isPaused || isComplete) return;
    
    const interval = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          setIsComplete(true);
          // Vibrate if supported
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isPaused, isComplete]);
  
  const formatTime = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  
  const progress = ((duration - remaining) / duration) * 100;
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="text-center">
          <p className="text-stone text-sm mb-2">{label}</p>
          
          {/* Circular progress */}
          <div className="relative w-48 h-48 mx-auto mb-6">
            <svg className="w-full h-full -rotate-90">
              <circle
                cx="96"
                cy="96"
                r="88"
                fill="none"
                stroke="#eae7e1"
                strokeWidth="8"
              />
              <circle
                cx="96"
                cy="96"
                r="88"
                fill="none"
                stroke={isComplete ? "#22c55e" : "#d97756"}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 88}
                strokeDashoffset={2 * Math.PI * 88 * (1 - progress / 100)}
                className="transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={clsx(
                "text-4xl font-bold tabular-nums",
                isComplete ? "text-green-600" : "text-ink"
              )}>
                {isComplete ? "Done!" : formatTime(remaining)}
              </span>
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex gap-3 justify-center">
            {!isComplete && (
              <button
                onClick={() => setIsPaused(!isPaused)}
                className="px-6 py-3 bg-[#d97756] text-white rounded-xl font-medium hover:bg-[#c56645] transition-colors"
              >
                {isPaused ? '▶ Resume' : '⏸ Pause'}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-6 py-3 bg-sand text-ink rounded-xl font-medium hover:bg-mist transition-colors"
            >
              {isComplete ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Mobile sticky ingredients panel
const MobileStickyIngredients = React.forwardRef<HTMLDivElement, {
  ingredients: any[];
  activeIngredients: number[];
  isExpanded: boolean;
  onToggle: () => void;
  servingRatio: number;
}>(({
  ingredients,
  activeIngredients,
  isExpanded,
  onToggle,
  servingRatio,
}, ref) => {
  const activeIngs = ingredients.filter((_, i) => activeIngredients.includes(i));
  const hasActive = activeIngs.length > 0;
  
  return (
    <div ref={ref} className="lg:hidden sticky top-14 z-10 -mx-4 px-4 pb-2 pt-2 bg-[#f5f3ef]/95 backdrop-blur-sm border-b border-mist">
      <button
        onClick={onToggle}
        className="w-full text-left"
      >
        <div className="bg-[#eae7e1] rounded-xl p-3 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-stone uppercase tracking-wide">
              {isExpanded ? 'All Ingredients' : hasActive ? 'For this step' : 'Ingredients'}
            </span>
            <span className="text-stone text-sm">
              {isExpanded ? '▲ collapse' : '▼ tap for all'}
            </span>
          </div>
          
          {isExpanded ? (
            // Full ingredient list - same chip style as collapsed, dynamic height
            (<div className="flex flex-wrap gap-1.5">
              {ingredients.map((ing: any, index: number) => {
                const isActive = activeIngredients.includes(index);
                const displayAmount = ing.amount 
                  ? (ing.amount * servingRatio).toFixed(1).replace(/\.0$/, '') 
                  : null;
                
                return (
                  <span 
                    key={ing.id || index}
                    className={clsx(
                      "inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm",
                      isActive 
                        ? "bg-white text-ink font-medium" 
                        : "bg-mist/50 text-stone"
                    )}
                  >
                    <span className={clsx(
                      "w-1.5 h-1.5 rounded-full flex-shrink-0",
                      isActive ? "bg-[#d97756]" : "bg-mist"
                    )} />
                    {displayAmount && (
                      <span className={isActive ? "text-stone" : "text-stone"}>{displayAmount}{ing.unit ? ` ${ing.unit}` : ''}</span>
                    )}{' '}
                    {ing.name}
                  </span>
                );
              })}
            </div>)
          ) : (
            // Compact view - dynamic height, shows all active ingredients
            (<div className="flex flex-wrap gap-1.5">
              {hasActive ? (
                activeIngs.map((ing: any) => {
                  const displayAmount = ing.amount 
                    ? (ing.amount * servingRatio).toFixed(1).replace(/\.0$/, '') 
                    : null;
                  return (
                    <span 
                      key={ing.id}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-lg text-sm text-ink"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#d97756] flex-shrink-0" />
                      {displayAmount && (
                        <span className="text-stone">{displayAmount}{ing.unit ? ` ${ing.unit}` : ''}</span>
                      )}{' '}
                      {ing.name}
                    </span>
                  );
                })
              ) : (
                <span className="text-stone text-sm italic py-1">No ingredients for this step</span>
              )}
            </div>)
          )}
        </div>
      </button>
    </div>
  )
});

function RecipeDetailPage() {
  const { recipeId } = Route.useParams();
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const [servings, setServings] = useState<number | null>(null);
  const [isFullWidth, _setIsFullWidth] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [displayedStepIndex, setDisplayedStepIndex] = useState(0); // Debounced version for ingredient panel
  const [breakdown, setBreakdown] = useState<string | null>(null);
  const [isBreakingDown, setIsBreakingDown] = useState(false);
  const [mobileIngredientsExpanded, setMobileIngredientsExpanded] = useState(false);
  const [activeTimer, setActiveTimer] = useState<{ duration: number; label: string } | null>(null);
  const [copied, setCopied] = useState(false);
  
  const stepsRef = useRef<(HTMLLIElement | null)[]>([]);
  const ingredientsRef = useRef<HTMLDivElement>(null);
  const ingredientDebounceRef = useRef<number | null>(null);
  const stickyPanelRef = useRef<HTMLDivElement>(null);
  
  // Redirect to login if not authenticated
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate({ to: '/' });
    }
  }, [isLoaded, isSignedIn, navigate]);
  
  // Helper for authenticated requests
  
  const recipe = useConvexQuery(api.recipes.getById, { id: recipeId as any });
  const isLoading = recipe === undefined;
  
  // AI Breakdown feature
  const breakdownMutation = useAction(api.recipeActions.generateBreakdown);
  
  const displayServings = servings ?? recipe?.servings ?? 4;
  const servingRatio = recipe?.servings ? displayServings / recipe.servings : 1;
  
  // Parse ingredients with normalized names for matching
  const ingredientsWithMeta = useMemo(() => {
    if (!recipe?.ingredients) return [];
    return recipe.ingredients.map((ing: any, index: number) => {
      // Normalize ingredient name for matching
      const name = ing.name.toLowerCase();
      // Extract key words for matching (remove common/descriptor words)
      const stopWords = [
        // Articles & prepositions
        'of', 'a', 'an', 'the', 'for', 'to', 'with',
        // Sizes & quantities  
        'large', 'small', 'medium', 'pinch', 'quarts', 'cups', 'tbsp', 'tsp', 'oz',
        // Preparation descriptors
        'fresh', 'dried', 'ground', 'chopped', 'minced', 'sliced', 'diced', 'very', 'thinly', 'finely',
        'heated', 'picked', 'drained', 'fronds',
        // Generic words
        'fine', 'dusting'
      ];
      const words = name
        .replace(/[,°]/g, ' ')  // Remove punctuation
        .split(/\s+/)
        .filter((w: string) => w.length > 2 && !stopWords.includes(w) && !/^\d+/.test(w));
      
      return {
        ...ing,
        index,
        normalizedName: name,
        matchWords: words,
        isOptional: ing.notes?.toLowerCase().includes('optional') || 
                    ing.name?.toLowerCase().includes('optional'),
        displayAmount: ing.amount 
          ? (ing.amount * servingRatio).toFixed(1).replace(/\.0$/, '') 
          : null,
      };
    });
  }, [recipe?.ingredients, servingRatio]);
  
  // Get ingredients for each step - use explicit mapping if available, fallback to text matching
  const stepIngredientMap = useMemo(() => {
    if (!recipe?.steps || !ingredientsWithMeta.length) return new Map();
    
    const map = new Map<number, number[]>();
    
    recipe.steps.forEach((step: any, stepIdx: number) => {
      // Use explicit ingredientIndices if available (from AI import)
      if (Array.isArray(step.ingredientIndices) && step.ingredientIndices.length > 0) {
        // Filter to valid indices only
        const validIndices = step.ingredientIndices.filter(
          (i: number) => typeof i === 'number' && i >= 0 && i < ingredientsWithMeta.length
        );
        map.set(stepIdx, validIndices);
        return;
      }
      
      // Fallback: text-based matching (less accurate, for older recipes without mapping)
      const usedIngredients: number[] = [];
      const text = step.instruction.toLowerCase();
      
      // Common words that often appear in multiple ingredients - need disambiguation
      const commonWords = new Set(['salt', 'water', 'oil', 'sugar', 'flour', 'butter', 'onion', 'garlic', 'pepper', 'cheese']);
      
      // Helper to get singular form
      const toSingular = (word: string) => {
        if (word.endsWith('ies')) return word.slice(0, -3) + 'y';
        if (word.endsWith('oes')) return word.slice(0, -2);
        if (word.endsWith('es')) return word.slice(0, -2);
        if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
        return word;
      };
      
      // Helper to check if word matches in text
      const wordMatchesInText = (word: string, text: string) => {
        const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const singular = toSingular(word);
        const singularEscaped = singular.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const patterns = [
          `\\b${escaped}\\b`,
          `\\b${escaped}s\\b`,
          `\\b${escaped}es\\b`,
          `\\b${singularEscaped}\\b`,
        ];
        return patterns.some(p => new RegExp(p, 'i').test(text));
      };
      
      // First pass: exact matches
      const exactMatches = new Set<number>();
      const matchedCommonWords = new Set<string>();
      
      ingredientsWithMeta.forEach((ing: any) => {
        const name = ing.normalizedName;
        const baseName = name.split(',')[0].trim();
        if (text.includes(name) || text.includes(baseName)) {
          exactMatches.add(ing.index);
          commonWords.forEach(cw => {
            if (name.includes(cw)) matchedCommonWords.add(cw);
          });
        }
      });
      
      exactMatches.forEach(idx => usedIngredients.push(idx));
      
      // Second pass: word-based matching
      const wordCandidates = new Map<string, { index: number; name: string; wordPosition: number }[]>();
      
      ingredientsWithMeta.forEach((ing: any) => {
        if (exactMatches.has(ing.index)) return;
        
        const words = ing.matchWords as string[];
        const name = ing.normalizedName;
        
        for (const word of words) {
          if (word.length >= 3) {
            const baseWord = toSingular(word);
            if ((commonWords.has(word) || commonWords.has(baseWord)) && 
                (matchedCommonWords.has(word) || matchedCommonWords.has(baseWord))) {
              continue;
            }
            
            if (wordMatchesInText(word, text)) {
              const matchKey = commonWords.has(baseWord) ? baseWord : (commonWords.has(word) ? word : null);
              if (matchKey) {
                if (!wordCandidates.has(matchKey)) wordCandidates.set(matchKey, []);
                const wordPosition = name.indexOf(baseWord) >= 0 ? name.indexOf(baseWord) : name.indexOf(word);
                wordCandidates.get(matchKey)!.push({ index: ing.index, name, wordPosition: wordPosition >= 0 ? wordPosition : 999 });
              } else {
                usedIngredients.push(ing.index);
              }
              return;
            }
          }
        }
      });
      
      wordCandidates.forEach((candidates) => {
        if (candidates.length >= 1) {
          candidates.sort((a, b) => a.wordPosition - b.wordPosition);
          usedIngredients.push(candidates[0].index);
        }
      });
      
      map.set(stepIdx, usedIngredients);
    });
    
    return map;
  }, [recipe?.steps, ingredientsWithMeta]);
  
  // Parse time strings to seconds
  const parseTimeToSeconds = useCallback((amount: string, unit: string): number => {
    const num = parseFloat(amount.replace('-', '.').split('-')[0]); // Take first number if range
    const u = unit.toLowerCase();
    if (u.startsWith('sec')) return num;
    if (u.startsWith('min')) return num * 60;
    if (u.startsWith('hr') || u.startsWith('hour')) return num * 3600;
    return num * 60; // default to minutes
  }, []);
  
  // Process instructions to link ingredients and times
  const processedSteps = useMemo(() => {
    if (!recipe?.steps || !ingredientsWithMeta.length) return [];
    
    return recipe.steps.map((step: any) => {
      let html = step.instruction;
      
      // Sort ingredients by name length (longest first) to match "flaky salt" before "salt"
      const sortedIngredients = [...ingredientsWithMeta].sort(
        (a, b) => b.name.length - a.name.length
      );
      
      // Track positions already matched to avoid overlapping replacements
      const matchedRanges: Array<{start: number, end: number}> = [];
      
      const isOverlapping = (start: number, end: number) => {
        return matchedRanges.some(range => 
          (start >= range.start && start < range.end) ||
          (end > range.start && end <= range.end) ||
          (start <= range.start && end >= range.end)
        );
      };
      
      // First pass: find all matches and their positions
      const replacements: Array<{start: number, end: number, text: string, replacement: string}> = [];
      
      for (const ing of sortedIngredients) {
        const fullName = ing.name;
        // Try full name first
        const fullRegex = new RegExp(`\\b(${fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
        let match;
        while ((match = fullRegex.exec(html)) !== null) {
          if (!isOverlapping(match.index, match.index + match[0].length)) {
            replacements.push({
              start: match.index,
              end: match.index + match[0].length,
              text: match[0],
              replacement: `<span class="ingredient-link">${match[0]}</span>`
            });
            matchedRanges.push({start: match.index, end: match.index + match[0].length});
          }
        }
        
        // Try individual significant words - only underline longer, meaningful words
        if (ing.matchWords.length > 0) {
          const word = ing.matchWords[0];
          if (word.length >= 4) { // Only underline words 4+ chars to avoid generic terms
            const wordRegex = new RegExp(`\\b(${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}e?s?)\\b`, 'gi');
            while ((match = wordRegex.exec(html)) !== null) {
              if (!isOverlapping(match.index, match.index + match[0].length)) {
                replacements.push({
                  start: match.index,
                  end: match.index + match[0].length,
                  text: match[0],
                  replacement: `<span class="ingredient-link">${match[0]}</span>`
                });
                matchedRanges.push({start: match.index, end: match.index + match[0].length});
              }
            }
          }
        }
      }
      
      // Sort replacements by position (reverse order) and apply
      replacements.sort((a, b) => b.start - a.start);
      for (const rep of replacements) {
        html = html.slice(0, rep.start) + rep.replacement + html.slice(rep.end);
      }
      
      // Link times (e.g., "30 mins", "1 hour", "5-10 minutes") - make them clickable
      html = html.replace(
        /(\d+(?:\s*-\s*\d+)?)\s*(mins?|minutes?|hrs?|hours?|seconds?|secs?)/gi,
        (_match: string, amount: string, unit: string) => {
          return `<button class="time-link" data-time="${amount}" data-unit="${unit}">⏱️ ${amount} ${unit}</button>`;
        }
      );
      
      return { ...step, html };
    });
  }, [recipe?.steps, ingredientsWithMeta]);
  
  // Handle timer clicks
  const handleTimerClick = useCallback((e: Event) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('time-link')) {
      const timeStr = target.dataset.time || '0';
      const unit = target.dataset.unit || 'min';
      const seconds = parseTimeToSeconds(timeStr, unit);
      setActiveTimer({ 
        duration: seconds, 
        label: `${timeStr} ${unit}` 
      });
    }
  }, [parseTimeToSeconds]);
  
  // Attach click handler for timer links
  useEffect(() => {
    document.addEventListener('click', handleTimerClick);
    return () => document.removeEventListener('click', handleTimerClick);
  }, [handleTimerClick]);
  
  // Track when we're programmatically scrolling (to disable scroll detection)
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  
  // Scroll to a specific step (used when clicking)
  const scrollToStep = useCallback((index: number) => {
    const el = stepsRef.current[index];
    if (!el) return;
    
    // Disable scroll detection during programmatic scroll
    isScrollingRef.current = true;
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    const isMobile = window.innerWidth < 1024;
    // Measure actual panel height dynamically
    const headerHeight = 56; // h-14 = 56px
    const panelHeight = stickyPanelRef.current?.offsetHeight || 0;
    const topOffset = isMobile ? headerHeight + panelHeight + 40 : 100; // 40px buffer to show prev step
    
    const elementTop = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: elementTop - topOffset,
      behavior: 'smooth'
    });
    
    setActiveStepIndex(index);
    setMobileIngredientsExpanded(false);
    
    // Re-enable scroll detection after animation completes
    scrollTimeoutRef.current = window.setTimeout(() => {
      isScrollingRef.current = false;
    }, 600); // smooth scroll typically takes ~500ms
  }, []);
  
  // Scroll sync - find which step is at the top of the visible area
  useEffect(() => {
    if (!stepsRef.current.length) return;
    
    let ticking = false;
    
    const updateActiveStep = () => {
      // Skip if we're programmatically scrolling
      if (isScrollingRef.current) {
        ticking = false;
        return;
      }
      
      // On mobile, account for header + sticky panel (measured dynamically)
      const isMobile = window.innerWidth < 1024;
      const headerHeight = 56;
      const panelHeight = stickyPanelRef.current?.offsetHeight || 0;
      const topOffset = isMobile ? headerHeight + panelHeight + 16 : 130;
      
      let bestIndex = 0;
      let bestDistance = -Infinity;
      
      stepsRef.current.forEach((el, index) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const distance = rect.top - topOffset;
        
        // Find the step that has passed the offset line (distance <= 0)
        // and is closest to it (largest negative value, i.e., just passed)
        if (distance <= 0 && distance > bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });
      
      // If no step has passed the line yet, use the first one
      if (bestDistance === -Infinity) {
        bestIndex = 0;
      }
      
      if (bestIndex !== activeStepIndex) {
        setActiveStepIndex(bestIndex);
        setMobileIngredientsExpanded(false);
      }
      
      ticking = false;
    };
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateActiveStep);
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initial check
    updateActiveStep();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [recipe?.steps, activeStepIndex]);
  
  // Debounce ingredient panel updates to prevent flickering during scroll
  useEffect(() => {
    if (ingredientDebounceRef.current) {
      clearTimeout(ingredientDebounceRef.current);
    }
    
    ingredientDebounceRef.current = window.setTimeout(() => {
      setDisplayedStepIndex(activeStepIndex);
    }, 300); // 300ms debounce - longer to allow scroll to settle
    
    return () => {
      if (ingredientDebounceRef.current) {
        clearTimeout(ingredientDebounceRef.current);
      }
    };
  }, [activeStepIndex]);
  
  // Get active ingredients for current step (use debounced index for panel)
  const activeIngredients = stepIngredientMap.get(displayedStepIndex) || [];
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f5f3ef]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="skeleton h-10 w-64 mb-8" />
          <div className="grid lg:grid-cols-[380px,1fr] xl:grid-cols-[420px,1fr] gap-8">
            <div className="skeleton h-96 rounded-2xl" />
            <div className="space-y-4">
              {[1, 2, 3].map(i => <div key={i} className="skeleton h-24 rounded-xl" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!recipe) {
    return (
      <div className="min-h-screen bg-[#f5f3ef] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-ink mb-4">Recipe Not Found</h1>
          <Link to="/recipes" className="text-coral-600 hover:underline">← Back to Recipes</Link>
        </div>
      </div>
    );
  }
  // Share recipe to clipboard
  const handleShareRecipe = async () => {
    if (!recipe) return;
    
    const ingredientList = recipe.ingredients?.map((ing: any) => {
      const amount = ing.amount ? String(ing.amount) : '';
      const unit = ing.unit || '';
      return `- ${amount} ${unit} ${ing.name}`.trim();
    }).join('\n') || 'No ingredients';
    
    const stepList = recipe.steps?.map((step: any, idx: number) => {
      return `${idx + 1}. ${step.instruction}`;
    }).join('\n\n') || 'No steps';
    
    const timeInfo = [];
    if (recipe.prepTime) timeInfo.push(`Prep: ${recipe.prepTime}m`);
    if (recipe.cookTime) timeInfo.push(`Cook: ${recipe.cookTime}m`);
    if (recipe.totalTime) timeInfo.push(`Total: ${recipe.totalTime}m`);
    
    const text = `${recipe.title}\n\n${timeInfo.length > 0 ? timeInfo.join(' | ') + '\n\n' : ''}Ingredients:\n${ingredientList}\n\nInstructions:\n${stepList}${recipe.sourceUrl ? '\n\nSource: ' + recipe.sourceUrl : ''}`;
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#f5f3ef]">
      {/* Timer Modal */}
      {activeTimer && (
        <TimerModal
          duration={activeTimer.duration}
          label={activeTimer.label}
          onClose={() => setActiveTimer(null)}
        />
      )}
      
      {/* Header */}
      <header className="bg-[#f5f3ef] border-b border-mist sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              {recipe.imageUrl && (
                <img src={recipe.imageUrl} alt="" className="w-10 h-10 rounded-lg object-cover" />
              )}
              <Link to="/recipes" className="text-stone hover:text-ink text-sm">
                ← Back
              </Link>
            </div>
            
            {recipe.sourceUrl && (
              <a
                href={recipe.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-sm text-stone hover:bg-sand rounded-lg"
              >
                Source ↗
              </a>
            )}
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Title */}
        <h1 className="text-2xl sm:text-3xl font-bold text-warm-900 mb-6">
          {recipe.title}
        </h1>
        
        {/* Controls */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={async () => {
              setIsBreakingDown(true);
              try {
                const result = await breakdownMutation({ recipeId: recipeId as any });
                setBreakdown(result.breakdown);
              } catch (err: any) {
                alert(err?.message || "Failed to generate breakdown");
              } finally {
                setIsBreakingDown(false);
              }
            }}
            disabled={isBreakingDown}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-mist text-ink hover:bg-snow text-sm font-medium disabled:opacity-50"
          >
            {isBreakingDown ? 'Analyzing...' : 'Breakdown'} 
            <span className="text-coral-500">✨</span>
          </button>
          
          {/* Share Button */}
          <button
            onClick={handleShareRecipe}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-mist text-ink hover:bg-snow text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {copied ? 'Copied!' : 'Share'}
          </button>
          <Link
            to="/recipes/$recipeId/edit"
            params={{ recipeId }}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-mist text-ink hover:bg-snow text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit
          </Link>
        </div>
        
        {/* Breakdown Panel */}
        {breakdown && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                ✨ AI Breakdown
              </h3>
              <button 
                onClick={() => setBreakdown(null)}
                className="text-amber-600 hover:text-amber-800 text-sm"
              >
                ✕ Close
              </button>
            </div>
            <div className="text-amber-900 text-sm whitespace-pre-wrap leading-relaxed">
              {breakdown}
            </div>
          </div>
        )}
        
        {/* Mobile Sticky Ingredients Panel */}
        <MobileStickyIngredients
          ref={stickyPanelRef}
          ingredients={ingredientsWithMeta}
          activeIngredients={activeIngredients}
          isExpanded={mobileIngredientsExpanded}
          onToggle={() => setMobileIngredientsExpanded(!mobileIngredientsExpanded)}
          servingRatio={servingRatio}
        />
        
        {/* Spacer for mobile sticky panel - prevents content from being hidden under panel */}
        <div className="lg:hidden h-2" />
        
        {/* Main Content - Split View */}
        <div className={clsx(
          'grid gap-8',
          isFullWidth ? 'grid-cols-1' : 'lg:grid-cols-[380px,1fr] xl:grid-cols-[420px,1fr]'
        )}>
          {/* Ingredients Column - Desktop only */}
          <div 
            ref={ingredientsRef}
            className={clsx(
              'bg-[#eae7e1] rounded-2xl p-5 h-fit hidden lg:block',
              !isFullWidth && 'lg:sticky lg:top-20'
            )}
          >
            {/* Serving Adjuster */}
            {recipe.servings && (
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-mist/50">
                <span className="text-sm text-stone">Servings</span>
                <div className="flex items-center border border-mist rounded-xl overflow-hidden bg-white">
                  <button
                    onClick={() => setServings(Math.max(1, displayServings - 1))}
                    className="w-9 h-9 flex items-center justify-center text-stone hover:bg-snow text-lg"
                  >
                    −
                  </button>
                  <span className="w-10 text-center font-semibold text-ink tabular-nums">
                    {displayServings}
                  </span>
                  <button
                    onClick={() => setServings(displayServings + 1)}
                    className="w-9 h-9 flex items-center justify-center text-stone hover:bg-snow text-lg"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
            
            {/* Ingredients List */}
            <ul className="space-y-2.5">
              {ingredientsWithMeta.map((ing: any) => {
                const isActive = activeIngredients.includes(ing.index);
                
                return (
                  <li 
                    key={ing.id} 
                    className={clsx(
                      'flex items-start gap-3 py-1 px-2 -mx-2 rounded-lg transition-colors duration-200',
                      isActive && 'bg-[#d97756]/10'
                    )}
                  >
                    {/* Bullet */}
                    <span 
                      className={clsx(
                        'w-2.5 h-2.5 rounded-full mt-2 flex-shrink-0 transition-all',
                        isActive ? 'bg-[#d97756] scale-125' : 
                          ing.isOptional ? 'bg-stone' : 'bg-[#d97756]'
                      )}
                    />
                    
                    {/* Content */}
                    <span className={clsx(
                      'text-ink leading-relaxed transition-colors',
                      isActive && 'text-warm-900 font-medium'
                    )}>
                      {ing.displayAmount && (
                        <span className="underline decoration-stone/60 underline-offset-2">
                          {ing.displayAmount}{ing.unit ? ` ${ing.unit}` : ''}
                        </span>
                      )}{' '}
                      {ing.name}
                      {ing.isOptional && !ing.name.includes('optional') && (
                        <span className="text-stone text-sm"> (optional)</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          
          {/* Instructions Column */}
          <div>
            {/* Mobile servings adjuster */}
            {recipe.servings && (
              <div className="flex items-center gap-3 mb-4 lg:hidden">
                <span className="text-sm text-stone">Servings</span>
                <div className="flex items-center border border-mist rounded-xl overflow-hidden bg-white">
                  <button
                    onClick={() => setServings(Math.max(1, displayServings - 1))}
                    className="w-9 h-9 flex items-center justify-center text-stone hover:bg-snow text-lg"
                  >
                    −
                  </button>
                  <span className="w-10 text-center font-semibold text-ink tabular-nums">
                    {displayServings}
                  </span>
                  <button
                    onClick={() => setServings(displayServings + 1)}
                    className="w-9 h-9 flex items-center justify-center text-stone hover:bg-snow text-lg"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
            
            <ol className="space-y-6">
              {processedSteps.map((step: any, index: number) => {
                const isActive = index === activeStepIndex;
                
                return (
                  <li 
                    key={step.id || index}
                    ref={el => stepsRef.current[index] = el}
                    style={{ scrollMarginTop: '180px' }}
                    className={clsx(
                      'flex gap-4 p-4 rounded-2xl transition-all duration-200',
                      isActive ? 'bg-white shadow-sm' : 'bg-transparent'
                    )}
                    onClick={() => scrollToStep(index)}
                  >
                    {/* Step Number */}
                    <div className={clsx(
                      'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors',
                      isActive ? 'bg-[#d97756] text-white' : 'bg-[#d97756]/20 text-[#d97756]'
                    )}>
                      {step.order || index + 1}
                    </div>
                    
                    {/* Step Content */}
                    <div 
                      className="flex-1 text-lg text-ink leading-relaxed pt-0.5 recipe-step-content"
                      dangerouslySetInnerHTML={{ __html: step.html }}
                    />
                  </li>
                );
              })}
            </ol>
            
            {/* Notes */}
            {recipe.notes && (
              <div className="mt-8 pt-6 border-t border-mist">
                <h3 className="font-semibold text-ink mb-2">Notes</h3>
                <p className="text-stone">{recipe.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Styles */}
      <style>{`
        .ingredient-link {
          text-decoration: underline;
          text-decoration-color: #d97756;
          text-underline-offset: 3px;
          text-decoration-thickness: 1.5px;
        }
        .time-link {
          display: inline;
          background: none;
          border: none;
          padding: 0;
          font: inherit;
          color: inherit;
          text-decoration: underline;
          text-decoration-color: #d97756;
          text-underline-offset: 3px;
          text-decoration-thickness: 1.5px;
          cursor: pointer;
          transition: background-color 0.15s;
          border-radius: 4px;
        }
        .time-link:hover {
          background-color: rgba(217, 119, 86, 0.15);
        }
        .time-link:active {
          background-color: rgba(217, 119, 86, 0.25);
        }
      `}</style>
    </div>
  );
}
